import type {
  DepartmentResponse,
  PlannerAllocation,
  PlannerPerspectiveResponse,
  ResourceResponse,
  RoleResponse,
} from '@timelog/pm-shared';

import type { PlannerT } from './plannerWindow';

/**
 * The pure row model behind the Resource Planner grid: perspective response
 * in, flat renderable rows out. Iterative throughout (BR-3 discipline — the
 * department tree is walked with an explicit stack).
 *
 * Expansion state has two defaults, so it travels as two sets:
 * - group rows (departments, unassigned demand, roles) are OPEN unless in
 *   `collapsedKeys`;
 * - resource rows are CLOSED unless in `expandedKeys`.
 */

export type PlannerTab = 'people' | 'demand';

export type PlannerCell = {
  hours: number;
  capacityHours: number;
  /** hours ÷ capacity; null when there is load but no capacity. */
  ratio: number | null;
};

export type PlannerRowKind =
  | 'deptGroup'
  | 'resource'
  | 'allocation'
  | 'unassignedGroup'
  | 'unassignedRole'
  | 'genericSlot'
  | 'roleGroup'
  | 'roleMember';

export type PlannerCellStyle = 'heat' | 'hours' | 'demand';

export type PlannerRow = {
  key: string;
  kind: PlannerRowKind;
  depth: 0 | 1 | 2;
  name: string;
  meta?: string;
  /** Resource rows: initials for the avatar, id for its colour. */
  avatar?: { id: string; initials: string };
  /** Allocation / slot rows: the root project id the colour dot derives from. */
  projectDotId?: string;
  /** Shown as a badge when < 100. */
  probability?: number;
  expandable: boolean;
  expanded: boolean;
  /** Utilisation over the whole window (bar + %); null hides the column. */
  utilRatio: number | null;
  col1: number | null;
  col2: number | null;
  col1Warn?: boolean;
  warn?: string;
  cellStyle: PlannerCellStyle;
  cells: PlannerCell[];
  /** Present on allocation/genericSlot rows — the Move popover's input. */
  allocation?: PlannerAllocation;
};

export type PlannerModelInput = {
  tab: PlannerTab;
  departments: DepartmentResponse[];
  resources: ResourceResponse[];
  roles: RoleResponse[];
  planner: PlannerPerspectiveResponse;
  buckets: readonly string[];
  collapsedKeys: ReadonlySet<string>;
  expandedKeys: ReadonlySet<string>;
  /** Row names and meta are user-visible copy. */
  t: PlannerT;
};

export type PlannerModel = {
  rows: PlannerRow[];
  /** Heat cells past capacity on resource/role rows — the toolbar chip. */
  overCapacityCells: number;
};

export const UNASSIGNED_GROUP_KEY = 'unassigned';

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!.toUpperCase())
    .slice(0, 2)
    .join('');

const zeroCells = (buckets: readonly string[]): PlannerCell[] =>
  buckets.map(() => ({ hours: 0, capacityHours: 0, ratio: null }));

const withRatios = (cells: PlannerCell[]): PlannerCell[] => {
  for (const cell of cells) {
    cell.ratio = cell.capacityHours > 0 ? cell.hours / cell.capacityHours : null;
  }
  return cells;
};

const sumInto = (target: PlannerCell[], source: readonly PlannerCell[]): void => {
  for (let index = 0; index < target.length; index += 1) {
    target[index]!.hours += source[index]!.hours;
    target[index]!.capacityHours += source[index]!.capacityHours;
  }
};

const totalRatio = (cells: readonly PlannerCell[]): number | null => {
  let hours = 0;
  let capacity = 0;
  for (const cell of cells) {
    hours += cell.hours;
    capacity += cell.capacityHours;
  }
  return capacity > 0 ? hours / capacity : null;
};

const countOver = (cells: readonly PlannerCell[]): number => {
  let over = 0;
  for (const cell of cells) {
    if (cell.hours > 0 && (cell.ratio === null || cell.ratio > 1)) over += 1;
  }
  return over;
};

/** Departments in parent-before-child (DFS) order — explicit stack, no recursion. */
const departmentsDepthFirst = (departments: DepartmentResponse[]): DepartmentResponse[] => {
  const childrenOf = new Map<string | null, DepartmentResponse[]>();
  for (const department of departments) {
    const siblings = childrenOf.get(department.parentId) ?? [];
    siblings.push(department);
    childrenOf.set(department.parentId, siblings);
  }
  const ordered: DepartmentResponse[] = [];
  const stack = [...(childrenOf.get(null) ?? [])].reverse();
  while (stack.length > 0) {
    const department = stack.pop()!;
    ordered.push(department);
    const children = childrenOf.get(department.id) ?? [];
    for (let index = children.length - 1; index >= 0; index -= 1) stack.push(children[index]!);
  }
  return ordered;
};

const allocationName = (allocation: PlannerAllocation): string =>
  allocation.workItemName === allocation.projectName
    ? allocation.projectName
    : allocation.workItemName;

const allocationMeta = (allocation: PlannerAllocation): string => {
  const short = allocation.projectNumber ?? allocation.projectName;
  return allocation.probability < 100 ? `${short} · ${String(allocation.probability)}%` : short;
};

export const buildPlannerModel = (input: PlannerModelInput): PlannerModel => {
  const { buckets, planner, t } = input;
  const bucketIndex = new Map(buckets.map((bucket, index) => [bucket, index]));

  // Per-resource utilisation cells straight off the response, zero-filled.
  const resourceCells = new Map<string, PlannerCell[]>();
  for (const entry of planner.resources) {
    const cells = zeroCells(buckets);
    for (const period of entry.periods) {
      const index = bucketIndex.get(period.period);
      if (index === undefined) continue;
      cells[index]!.hours = period.workloadHours;
      cells[index]!.capacityHours = period.capacityHours;
    }
    resourceCells.set(entry.resourceId, withRatios(cells));
  }

  const allocationCells = (allocation: PlannerAllocation): PlannerCell[] => {
    const cells = zeroCells(buckets);
    for (const period of allocation.periods) {
      const index = bucketIndex.get(period.period);
      if (index === undefined) continue;
      cells[index]!.hours = period.hours;
    }
    return cells;
  };

  const named = planner.allocations.filter((entry) => entry.resourceKind === 'NAMED');
  const generic = planner.allocations.filter((entry) => entry.resourceKind === 'GENERIC');
  const namedByResource = new Map<string, PlannerAllocation[]>();
  for (const allocation of named) {
    if (allocation.resourceId === null) continue;
    const list = namedByResource.get(allocation.resourceId) ?? [];
    list.push(allocation);
    namedByResource.set(allocation.resourceId, list);
  }

  const rows: PlannerRow[] = [];
  let overCapacityCells = 0;

  if (input.tab === 'people') {
    for (const department of departmentsDepthFirst(input.departments)) {
      const members = input.resources.filter((resource) => resource.departmentId === department.id);
      if (members.length === 0) continue;

      const groupCells = zeroCells(buckets);
      let groupAllocated = 0;
      let groupRegistered = 0;
      for (const member of members) {
        sumInto(groupCells, resourceCells.get(member.id) ?? zeroCells(buckets));
        for (const allocation of namedByResource.get(member.id) ?? []) {
          groupAllocated += allocation.allocatedBudget;
          groupRegistered += allocation.registeredHours;
        }
      }
      withRatios(groupCells);

      const groupKey = `dept:${department.id}`;
      const groupExpanded = !input.collapsedKeys.has(groupKey);
      rows.push({
        key: groupKey,
        kind: 'deptGroup',
        depth: 0,
        name: department.name,
        meta: t('model.People', { count: members.length }),
        expandable: true,
        expanded: groupExpanded,
        utilRatio: totalRatio(groupCells),
        col1: groupAllocated,
        col2: groupRegistered,
        cellStyle: 'heat',
        cells: groupCells,
      });
      if (!groupExpanded) continue;

      for (const member of members) {
        const cells = resourceCells.get(member.id) ?? withRatios(zeroCells(buckets));
        const allocations = namedByResource.get(member.id) ?? [];
        overCapacityCells += countOver(cells);
        const expanded = input.expandedKeys.has(member.id);
        const primaryRole = member.roles.find((role) => role.isPrimary) ?? member.roles[0];
        rows.push({
          key: member.id,
          kind: 'resource',
          depth: 1,
          name: member.name,
          meta: primaryRole?.roleName,
          avatar: { id: member.id, initials: initialsOf(member.name) },
          expandable: allocations.length > 0,
          expanded,
          utilRatio: totalRatio(cells),
          col1: allocations.reduce((sum, entry) => sum + entry.allocatedBudget, 0),
          col2: allocations.reduce((sum, entry) => sum + entry.registeredHours, 0),
          cellStyle: 'heat',
          cells,
        });
        if (!expanded) continue;

        for (const allocation of allocations) {
          const drillCells = allocationCells(allocation);
          for (let index = 0; index < drillCells.length; index += 1) {
            drillCells[index]!.capacityHours = cells[index]!.capacityHours;
          }
          rows.push({
            key: allocation.allocationId,
            kind: 'allocation',
            depth: 2,
            name: allocationName(allocation),
            meta: allocationMeta(allocation),
            projectDotId: allocation.rootProjectId,
            probability: allocation.probability,
            expandable: false,
            expanded: false,
            utilRatio: null,
            col1: allocation.allocatedBudget,
            col2: allocation.registeredHours,
            cellStyle: 'hours',
            cells: drillCells,
            allocation,
          });
        }
      }
    }

    if (generic.length > 0) {
      const groupCells = zeroCells(buckets);
      for (const allocation of generic) sumInto(groupCells, allocationCells(allocation));
      const groupExpanded = !input.collapsedKeys.has(UNASSIGNED_GROUP_KEY);
      rows.push({
        key: UNASSIGNED_GROUP_KEY,
        kind: 'unassignedGroup',
        depth: 0,
        name: t('model.UnassignedDemand'),
        meta: t('model.GenericSlots', { count: generic.length }),
        expandable: true,
        expanded: groupExpanded,
        utilRatio: null,
        col1: generic.reduce((sum, entry) => sum + entry.allocatedBudget, 0),
        col1Warn: true,
        col2: null,
        cellStyle: 'demand',
        cells: groupCells,
      });

      if (groupExpanded) {
        const roleNames = new Map(input.roles.map((role) => [role.id, role.name]));
        const byRole = new Map<string, PlannerAllocation[]>();
        for (const allocation of generic) {
          const roleId = allocation.roleId ?? 'unknown';
          const list = byRole.get(roleId) ?? [];
          list.push(allocation);
          byRole.set(roleId, list);
        }
        for (const [roleId, slots] of byRole) {
          const roleCells = zeroCells(buckets);
          for (const slot of slots) sumInto(roleCells, allocationCells(slot));
          const roleKey = `unassigned:${roleId}`;
          const roleExpanded = !input.collapsedKeys.has(roleKey);
          rows.push({
            key: roleKey,
            kind: 'unassignedRole',
            depth: 1,
            name: roleNames.get(roleId) ?? t('model.UnknownCompetency'),
            expandable: true,
            expanded: roleExpanded,
            utilRatio: null,
            col1: slots.reduce((sum, entry) => sum + entry.allocatedBudget, 0),
            col1Warn: true,
            col2: null,
            cellStyle: 'demand',
            cells: roleCells,
          });
          if (!roleExpanded) continue;
          for (const slot of slots) {
            rows.push({
              key: slot.allocationId,
              kind: 'genericSlot',
              depth: 2,
              name: allocationName(slot),
              meta: allocationMeta(slot),
              projectDotId: slot.rootProjectId,
              probability: slot.probability,
              expandable: false,
              expanded: false,
              utilRatio: null,
              col1: slot.allocatedBudget,
              col1Warn: true,
              col2: null,
              cellStyle: 'demand',
              cells: allocationCells(slot),
              allocation: slot,
            });
          }
        }
      }
    }

    return { rows, overCapacityCells };
  }

  // --- Competency demand tab -------------------------------------------------
  const holdersByRole = new Map<string, ResourceResponse[]>();
  for (const resource of input.resources) {
    for (const role of resource.roles) {
      const holders = holdersByRole.get(role.roleId) ?? [];
      holders.push(resource);
      holdersByRole.set(role.roleId, holders);
    }
  }
  const genericByRole = new Map<string, PlannerAllocation[]>();
  for (const allocation of generic) {
    if (allocation.roleId === null) continue;
    const list = genericByRole.get(allocation.roleId) ?? [];
    list.push(allocation);
    genericByRole.set(allocation.roleId, list);
  }

  const orderedRoles: RoleResponse[] = [...input.roles].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  for (const role of orderedRoles) {
    const holders = holdersByRole.get(role.id) ?? [];
    const slots = genericByRole.get(role.id) ?? [];
    if (holders.length === 0 && slots.length === 0) continue;

    // Demand = holders' full workload (ALL role associations count — the
    // server's PM-2056 semantics) + the role's generic demand.
    const demandCells = zeroCells(buckets);
    for (const holder of holders) {
      sumInto(demandCells, resourceCells.get(holder.id) ?? zeroCells(buckets));
    }
    // Generic demand adds hours without capacity.
    for (const slot of slots) {
      const cells = allocationCells(slot);
      for (let index = 0; index < demandCells.length; index += 1) {
        demandCells[index]!.hours += cells[index]!.hours;
      }
    }
    withRatios(demandCells);
    overCapacityCells += countOver(demandCells);

    let demandTotal = 0;
    let capacityTotal = 0;
    for (const cell of demandCells) {
      demandTotal += cell.hours;
      capacityTotal += cell.capacityHours;
    }

    const roleKey = `role:${role.id}`;
    const roleExpanded = !input.collapsedKeys.has(roleKey);
    const noHolders = holders.length === 0 && demandTotal > 0;
    rows.push({
      key: roleKey,
      kind: 'roleGroup',
      depth: 0,
      name: role.name,
      meta: t('model.People', { count: holders.length }),
      expandable: true,
      expanded: roleExpanded,
      utilRatio: totalRatio(demandCells),
      col1: demandTotal,
      col2: capacityTotal,
      warn: noHolders ? t('model.NoStaffHoldCompetency') : undefined,
      cellStyle: 'heat',
      cells: demandCells,
    });
    if (!roleExpanded) continue;

    for (const holder of holders) {
      const cells = resourceCells.get(holder.id) ?? withRatios(zeroCells(buckets));
      let holderHours = 0;
      let holderCapacity = 0;
      for (const cell of cells) {
        holderHours += cell.hours;
        holderCapacity += cell.capacityHours;
      }
      rows.push({
        key: `${roleKey}:${holder.id}`,
        kind: 'roleMember',
        depth: 1,
        name: holder.name,
        avatar: { id: holder.id, initials: initialsOf(holder.name) },
        expandable: false,
        expanded: false,
        utilRatio: totalRatio(cells),
        col1: holderHours,
        col2: holderCapacity,
        cellStyle: 'hours',
        cells,
      });
    }
    for (const slot of slots) {
      rows.push({
        key: `${roleKey}:${slot.allocationId}`,
        kind: 'genericSlot',
        depth: 1,
        name: t('model.GenericSlot'),
        meta: `${allocationMeta(slot)} · ${allocationName(slot)}`,
        projectDotId: slot.rootProjectId,
        probability: slot.probability,
        expandable: false,
        expanded: false,
        utilRatio: null,
        col1: slot.allocatedBudget,
        col1Warn: true,
        col2: null,
        cellStyle: 'demand',
        cells: allocationCells(slot),
        allocation: slot,
      });
    }
  }

  return { rows, overCapacityCells };
};
